import { useState, useEffect, useRef } from 'react';
import PeoplePicker from '../../../components/PeoplePicker';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { safeJson, fmtDate, surveyBadgeClass } from '../../../utils';
import type { ChangeRequest, ChangeLog, SurveyRecipient, SurveyLogData, LessonsLearned, GraphUser } from '../../../types';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function ReviewSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [surveyLogs, setSurveyLogs] = useState<ChangeLog[]>([]);
  const [recipients, setRecipients] = useState<SurveyRecipient[]>([]);
  const [surveysSent, setSurveysSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [submittingClosure, setSubmittingClosure] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const existingLL = safeJson<Partial<LessonsLearned>>(record.lessonsLearned, {});
  const existingRecipients = safeJson<SurveyRecipient[]>(record.surveyRecipients, []);

  const DRAFT_KEY = `ll_draft_${cid}`;
  const draft = safeJson<Partial<LessonsLearned>>(localStorage.getItem(DRAFT_KEY) ?? '{}', {});

  const [wentWell,        setWentWell]        = useState(draft.wentWell        ?? existingLL.wentWell        ?? '');
  const [improvements,    setImprovements]    = useState(draft.improvements    ?? existingLL.improvements    ?? '');
  const [recommendations, setRecommendations] = useState(draft.recommendations ?? existingLL.recommendations ?? '');

  async function loadSurveyLogs() {
    try {
      const data = await api.get<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs?type=Survey Response`);
      setSurveyLogs(data);
      if (data.every(r => safeJson<SurveyLogData>(r.logData, {} as SurveyLogData).status === 'Responded')) {
        if (timerRef.current) clearInterval(timerRef.current);
      }
    } catch {}
  }

  useEffect(() => {
    if (existingRecipients.length > 0) {
      setSurveysSent(true);
      loadSurveyLogs();
      timerRef.current = setInterval(loadSurveyLogs, 60000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [cid]);

  function addRecipient(user: GraphUser) {
    if (recipients.some(r => r.email === user.userPrincipalName)) { showToast(`${user.displayName} already added.`, 'warn'); return; }
    setRecipients(p => [...p, { name: user.displayName, email: user.userPrincipalName, department: user.department ?? '' }]);
  }

  async function sendSurveys(toSend: SurveyRecipient[]) {
    if (toSend.length === 0) { showToast('Add at least one recipient.', 'warn'); return; }
    setSending(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/surveys`, {
        surveyRecipients: JSON.stringify(toSend),
      });
      setSurveysSent(true);
      setRecipients([]);
      showToast('Surveys sent.', 'success');
      await loadSurveyLogs();
      if (!timerRef.current) timerRef.current = setInterval(loadSurveyLogs, 60000);
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setSending(false); }
  }

  function saveDraft() {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ wentWell, improvements, recommendations }));
    showToast('Draft saved.', 'success');
  }

  const responded = surveyLogs.filter(r => safeJson<SurveyLogData>(r.logData, {} as SurveyLogData).status === 'Responded');
  const closureEligible = !!(wentWell && improvements && responded.length > 0 && record.surveyStatus);

  const isLocked = record.status === 'Pending Sponsor Closure' || record.status === 'Closed';

  async function submitForClosure() {
    setSubmittingClosure(true);
    try {
      await api.patch(`/changes/${encodeURIComponent(cid)}/lessons`, {
        lessonsLearned: JSON.stringify({ wentWell, improvements, recommendations }),
      });
      await api.patch(`/changes/${encodeURIComponent(cid)}/status`, {
        status: 'Pending Sponsor Closure',
        closureSubmittedDate: new Date().toISOString(),
      });
      localStorage.removeItem(DRAFT_KEY);
      showToast(`Closure sign-off request sent to ${record.sponsorName}.`, 'success');
      await onRefresh();
    } catch (err) { showToast((err as Error).message, 'error'); }
    finally { setSubmittingClosure(false); }
  }

  return (
    <>
      {/* Summary */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-chart-line" aria-hidden="true" /> Step 7 — Post-Implementation Review</h2>
        </div>
        <div className="section-panel__body">
          <div className="field-display-grid">
            <div className="field-display"><div className="field-display__label">Go-Live Date</div><div className="field-display__value">{fmtDate(record.confirmedGolive)}</div></div>
            <div className="field-display"><div className="field-display__label">Survey Status</div><div className="field-display__value">
              {record.surveyStatus ? <span className={`badge ${surveyBadgeClass(record.surveyStatus)}`}>{record.surveyStatus}</span> : '—'}
            </div></div>
            <div className="field-display"><div className="field-display__label">Average Score</div><div className="field-display__value">{record.surveyScore != null ? `${record.surveyScore} / 10` : '—'}</div></div>
          </div>
        </div>
      </div>

      {/* Survey distribution */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-envelope-open-text" aria-hidden="true" /> Survey Distribution</h2>
        </div>
        <div className="section-panel__body">
          {/* Initial state: no surveys sent yet (fallback if auto-send didn't fire) */}
          {!surveysSent && (
            <>
              <p className="form-hint" style={{ marginBottom: 12 }}>Surveys will be sent automatically to affected stakeholders. You may also add additional recipients below.</p>
              <PeoplePicker onSelect={addRecipient} placeholder="Search to add survey recipient…" />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, marginBottom: 12 }}>
                {recipients.map((r, i) => (
                  <div key={r.email} className="stakeholder-row">
                    <div className="stakeholder-row__avatar">{r.name.charAt(0).toUpperCase()}</div>
                    <div className="stakeholder-row__info">
                      <div className="stakeholder-row__name">{r.name}</div>
                      <div className="stakeholder-row__meta">{r.email}</div>
                    </div>
                    <button className="stakeholder-row__remove" onClick={() => setRecipients(p => p.filter((_, j) => j !== i))}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
                  </div>
                ))}
              </div>
              {recipients.length > 0 && (
                <button className={`btn btn--primary${sending ? ' is-loading' : ''}`} onClick={() => sendSurveys(recipients)} disabled={sending}>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Send Surveys
                </button>
              )}
            </>
          )}

          {/* Stuck state: saved but emails not delivered */}
          {surveysSent && surveyLogs.length === 0 && (
            <>
              <div className="alert alert--warn" style={{ marginBottom: 16 }}>
                <span className="alert__icon"><i className="fa-solid fa-triangle-exclamation" aria-hidden="true" /></span>
                <div className="alert__body">
                  <div className="alert__title">Survey invitations not yet sent</div>
                  <p>Recipients have been saved but invitations were not delivered. Click below to send them now.</p>
                </div>
              </div>
              <button className={`btn btn--primary${sending ? ' is-loading' : ''}`} onClick={() => sendSurveys(existingRecipients)} disabled={sending}>
                <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Send Survey Invitations
              </button>
            </>
          )}

          {/* Normal state: surveys sent, table + add more */}
          {surveyLogs.length > 0 && (
            <>
              <div className="table-wrapper" style={{ marginBottom: 20 }}>
                <table className="table" aria-label="Survey recipients">
                  <thead><tr><th>Name</th><th>Email</th><th>Status</th></tr></thead>
                  <tbody>
                    {surveyLogs.map(r => {
                      const d = safeJson<SurveyLogData>(r.logData, {} as SurveyLogData);
                      return (
                        <tr key={r.id}>
                          <td data-label="Name">{r.loggedByName}</td>
                          <td data-label="Email">{r.loggedByEmail}</td>
                          <td data-label="Status">{d.status === 'Responded' ? <span className="badge badge--accent">Responded</span> : <span className="badge badge--warn">Pending</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                <p style={{ margin: '0 0 10px', fontSize: '0.875rem', fontWeight: 600, color: 'var(--ink-dim)' }}>
                  <i className="fa-solid fa-user-plus" aria-hidden="true" style={{ marginRight: 6 }} />
                  Add More Recipients
                </p>
                <PeoplePicker onSelect={addRecipient} placeholder="Search to add additional survey recipient…" />
                {recipients.length > 0 && (
                  <>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12, marginBottom: 12 }}>
                      {recipients.map((r, i) => (
                        <div key={r.email} className="stakeholder-row">
                          <div className="stakeholder-row__avatar">{r.name.charAt(0).toUpperCase()}</div>
                          <div className="stakeholder-row__info">
                            <div className="stakeholder-row__name">{r.name}</div>
                            <div className="stakeholder-row__meta">{r.email}</div>
                          </div>
                          <button className="stakeholder-row__remove" onClick={() => setRecipients(p => p.filter((_, j) => j !== i))}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
                        </div>
                      ))}
                    </div>
                    <button className={`btn btn--secondary${sending ? ' is-loading' : ''}`} onClick={() => sendSurveys(recipients)} disabled={sending}>
                      <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Send Survey
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lessons learned */}
      <div className="section-panel" style={{ marginBottom: 16 }}>
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-lightbulb" aria-hidden="true" /> Lessons Learned</h2>
        </div>
        <div className="section-panel__body">
          <div className="form-grid">
            {([
              ['ll-went-well', 'What went well?', wentWell, setWentWell, true],
              ['ll-improvements', 'What could have been done better?', improvements, setImprovements, true],
              ['ll-recommendations', 'Recommendations for future changes', recommendations, setRecommendations, false],
            ] as const).map(([id, label, val, setter, required]) => (
              <div key={id} className="form-group form-group--full">
                <label className="form-label" htmlFor={id}>
                  {label} {required ? <span className="required">*</span> : <span className="badge badge--muted" style={{ fontWeight: 400, marginLeft: 6 }}>Optional</span>}
                </label>
                <textarea id={id} className="form-control" rows={4} value={val}
                  readOnly={isLocked}
                  onChange={e => !isLocked && (setter as React.Dispatch<React.SetStateAction<string>>)(e.target.value)} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            {isLocked ? (
              <div className="alert alert--success" style={{ flex: 1, padding: '10px 14px' }}>
                <span className="alert__icon"><i className="fa-solid fa-circle-check" aria-hidden="true" /></span>
                <div className="alert__body">Submitted for closure. Awaiting sponsor sign-off from <strong>{record.sponsorName}</strong>.</div>
              </div>
            ) : (
              <>
                <button className="btn btn--secondary" onClick={saveDraft}>
                  <i className="fa-solid fa-floppy-disk" aria-hidden="true" /> Save Draft
                </button>
                <button className={`btn btn--primary${submittingClosure ? ' is-loading' : ''}`} onClick={submitForClosure} disabled={!closureEligible || submittingClosure}>
                  <i className="fa-solid fa-paper-plane" aria-hidden="true" /> Submit for Closure
                </button>
                <span className="form-hint">
                  {closureEligible ? 'Ready to submit for closure.' : 'Complete lessons learned and ensure at least one survey response before submitting.'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
